/*
  bundle-builder.js — "SETUP COMPLET" kit composer (S10).

  Guarded custom element keyed to its own [data-section-id]. Responsibilities:
    - Live total: sum the (per-line) prices of the currently-INCLUDED, available
      components when the optional include toggles change.
    - One-shot multi-add: POST { items:[{id,quantity}...] } to /cart/add.js,
      requesting the cart sections, then hand the response to the theme cart
      (cart-drawer OR cart-notification — same API) via renderContents() so the
      drawer/notification opens. Mirrors product-form.js so there is a single
      add-to-cart code path/state owner (the real Shopify cart).
    - Availability gate: if any INCLUDED component is unavailable / out of stock,
      the CTA is disabled and a note is shown. With toggles on, a sold-out row is
      unticked and disabled instead (syncToggle), so it never blocks the rest.
    - Current-product row: when the PAGE's own product is a component, its row
      carries [data-bundle-current] and follows the buy box — on variantChange
      (product-info.js) its variant id / price / availability / label / image are
      updated and the total recomputed. Display strings only; the cart add uses
      the variant id, never option values.

  No fabricated discount. The total is a genuine sum of real variant prices.
  Prices are integer cents (Shopify money), formatted server-side via a hidden
  per-component money string so currency/locale match the storefront exactly.
*/
if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.variantUnsubscriber = null;
      }

      connectedCallback() {
        this.cta = this.querySelector('[data-bundle-cta]');
        this.totalEl = this.querySelector('[data-bundle-total]');
        this.countEl = this.querySelector('[data-bundle-count]');
        this.freeshipEl = this.querySelector('[data-bundle-freeship]');
        // RON-gated free-shipping threshold in cents (0 = disabled / non-RON market).
        this.freeshipMin = parseInt(this.dataset.freeshipMin, 10) || 0;
        this.summary = this.querySelector('[data-bundle-summary]');
        this.liveRegion = this.querySelector('[data-bundle-live]');
        this.toggles = Array.from(this.querySelectorAll('[data-bundle-toggle]'));

        // The theme cart UI (drawer when cart_type=drawer, else notification).
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

        this.toggles.forEach((cb) => cb.addEventListener('change', this.onToggle));
        if (this.cta) this.cta.addEventListener('click', this.onSubmit);

        // Only kits that contain the page's own product listen for buy-box changes.
        if (
          this.querySelector('[data-bundle-current]') &&
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined'
        ) {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.refresh();
      }

      disconnectedCallback() {
        this.toggles.forEach((cb) => cb.removeEventListener('change', this.onToggle));
        if (this.cta) this.cta.removeEventListener('click', this.onSubmit);
        if (typeof this.variantUnsubscriber === 'function') this.variantUnsubscriber();
        this.variantUnsubscriber = null;
      }

      /* All component rows (whether toggleable or always-on). */
      get rows() {
        return Array.from(this.querySelectorAll('[data-bundle-item]'));
      }

      isIncluded(row) {
        const cb = row.querySelector('[data-bundle-toggle]');
        // No toggle => always included. With a toggle => follow its state.
        return cb ? cb.checked : true;
      }

      isAvailable(row) {
        return row.dataset.available === 'true';
      }

      /* Items eligible to be added: included AND available AND have a variant id. */
      includedAvailable() {
        return this.rows.filter((r) => this.isIncluded(r) && this.isAvailable(r) && r.dataset.variantId);
      }

      onToggle(event) {
        const row = event.target.closest('[data-bundle-item]');
        if (row) row.classList.toggle('is-excluded', !event.target.checked);
        // The shopper decided: availability changes no longer tick/untick this row.
        delete event.target.dataset.autoExcluded;
        this.refresh();
      }

      /*
        A sold-out row is never included (it would only block the kit): untick and
        disable its toggle, marked data-auto-excluded. When it becomes available again,
        re-tick it, but only if the untick was ours, not the shopper's.
      */
      syncToggle(row, available) {
        const cb = row.querySelector('[data-bundle-toggle]');
        if (!cb) return;
        cb.disabled = !available;
        if (!available && cb.checked) {
          cb.checked = false;
          cb.dataset.autoExcluded = '';
        } else if (available && 'autoExcluded' in cb.dataset) {
          cb.checked = true;
          delete cb.dataset.autoExcluded;
        }
        row.classList.toggle('is-excluded', !cb.checked);
      }

      /*
        variantChange is global: quick-add / other sections publish it for OTHER
        products too. Act only when the variant belongs to the current-product row
        (data-variant-ids, rendered by Liquid — variant.product_id isn't always in
        the payload). A null variant (unavailable combination) changes nothing.
        Never throw: pubsub publish() maps subscribers synchronously, so an error
        here would stop the ones after us (sticky-atc, gallery…).
        Order matters: the dataset writes (what the total and the gate read) come
        first; the display work after them may fail, and refresh() runs in `finally`
        so the total and the CTA never stay stale because of it.
      */
      onVariantChange(event) {
        let synced = false;
        try {
          const variant = event && event.data && event.data.variant;
          const row = this.querySelector('[data-bundle-current]');
          if (!variant || !row) return;
          if (!(row.dataset.variantIds || '').split(',').includes(String(variant.id))) return;

          const state = this.variantRowState(variant);
          row.dataset.variantId = state.id;
          row.dataset.priceCents = String(state.cents);
          row.dataset.available = String(state.available);
          synced = true;

          row.classList.toggle('is-unavailable', !state.available);
          this.syncToggle(row, state.available);
          // "Stoc epuizat" flag: always rendered on this row, shown only when sold out.
          const flagEl = row.querySelector('[data-bundle-flag]');
          if (flagEl) flagEl.hidden = state.available;

          // Label is display-only (may be the translated value) — logic uses the id.
          const titleEl = row.querySelector('[data-bundle-variant-title]');
          if (titleEl && state.title) titleEl.textContent = state.title;

          const priceEl = row.querySelector('[data-bundle-price]');
          if (priceEl) {
            priceEl.textContent = this.formatMoney(state.cents);
            if (state.wasCents > state.cents) {
              const was = document.createElement('s');
              was.textContent = this.formatMoney(state.wasCents);
              priceEl.appendChild(was);
            }
          }

          // Variant image when it has one; otherwise keep the current picture and alt.
          // The alt follows the picture, but only when the variant provides one.
          const img = row.querySelector('.bundle-item__media img');
          if (img && state.imageSrc) {
            img.removeAttribute('srcset');
            img.src = this.resizedUrl(state.imageSrc, 160);
            if (state.imageAlt) img.alt = state.imageAlt;
          }
        } catch (err) {
          console.error('[bundle-builder] variant sync failed', err);
        } finally {
          if (synced) {
            try {
              this.refresh();
            } catch (err) {
              console.error('[bundle-builder] refresh failed', err);
            }
          }
        }
      }

      /* Pure: what a variantChange payload means for the current-product row. */
      variantRowState(variant) {
        const media = variant.featured_media && variant.featured_media.preview_image;
        return {
          id: String(variant.id),
          cents: parseInt(variant.price, 10) || 0,
          wasCents: parseInt(variant.compare_at_price, 10) || 0,
          available: variant.available !== false,
          title: variant.title || '',
          imageSrc: (media && media.src) || (variant.featured_image && variant.featured_image.src) || '',
          imageAlt: String((variant.featured_media && variant.featured_media.alt) || '').trim(),
        };
      }

      resizedUrl(src, width) {
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', width);
          return url.toString();
        } catch (e) {
          return src;
        }
      }

      /* Recompute live total, count, and the availability gate. */
      refresh() {
        const included = this.rows.filter((r) => this.isIncluded(r));
        const addable = this.includedAvailable();

        // Live total in cents, summed from each row's per-line price.
        const cents = addable.reduce((sum, r) => sum + (parseInt(r.dataset.priceCents, 10) || 0), 0);
        if (this.totalEl) this.totalEl.textContent = this.formatMoney(cents);

        // Free-shipping note: show once the kit total clears the (RON-gated) threshold.
        if (this.freeshipEl) this.freeshipEl.hidden = !(this.freeshipMin > 0 && cents >= this.freeshipMin);

        if (this.countEl) {
          const n = addable.length;
          this.countEl.textContent = this.countEl.dataset.template
            ? this.countEl.dataset.template.replace('[count]', n)
            : String(n);
        }

        // Blocker: an INCLUDED component is unavailable, or nothing addable.
        const hasUnavailableIncluded = included.some((r) => !this.isAvailable(r));
        const nothingToAdd = addable.length === 0;
        const blocked = hasUnavailableIncluded || nothingToAdd;

        if (this.summary) this.summary.classList.toggle('has-blocker', blocked);
        if (this.cta) {
          this.cta.disabled = blocked;
          this.cta.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        }
      }

      /*
        Mirror the storefront's `money` filter using a server-rendered sample of
        a known amount (1.234,56 → data-money-sample). We infer the thousands
        separator, decimal separator, decimal count, and the prefix/suffix
        (currency symbol / code) from that one string, then re-format `cents`
        identically. Falls back to a plain number if the sample is missing.
      */
      get moneyFmt() {
        if (this._moneyFmt) return this._moneyFmt;

        const sample = this.dataset.moneySample || '';
        // Default: "1234.56" style, no affixes.
        let fmt = { prefix: '', suffix: '', preGap: '', postGap: '', thousands: ',', decimal: '.', decimals: 2 };

        // Locate the numeric core of the sample (digits + separators).
        const match = sample.match(/[\d.,\s ']+/);
        if (match) {
          // match[0] can carry the whitespace between the number and its currency
          // ("1.234,56 lei"). Measure prefix/suffix from the TRIMMED core, otherwise
          // that gap is swallowed and the output reads "249,99lei".
          const lead = match[0].length - match[0].replace(/^\s+/, '').length;
          const trail = match[0].length - match[0].replace(/\s+$/, '').length;
          const coreStart = match.index + lead;
          const coreEnd = match.index + match[0].length - trail;
          const numStr = sample.slice(coreStart, coreEnd);
          const rawPrefix = sample.slice(0, coreStart);
          const rawSuffix = sample.slice(coreEnd);
          fmt.prefix = rawPrefix.replace(/\s+$/, '');
          fmt.preGap = fmt.prefix ? rawPrefix.slice(fmt.prefix.length) : '';
          fmt.suffix = rawSuffix.replace(/^\s+/, '');
          fmt.postGap = fmt.suffix ? rawSuffix.slice(0, rawSuffix.length - fmt.suffix.length) : '';

          // The sample value is 1234.56 → the last separator before 1-2 trailing
          // digits is the decimal separator; an earlier one is thousands.
          const sepMatches = numStr.match(/[.,\s ']/g) || [];
          const lastSep = sepMatches[sepMatches.length - 1];
          const tail = numStr.split(/[.,\s ']/).pop();

          if (lastSep && tail.length <= 2 && /^\d+$/.test(tail)) {
            fmt.decimal = lastSep;
            fmt.decimals = tail.length;
            const firstSep = sepMatches.find((s) => s !== lastSep);
            fmt.thousands = firstSep || (lastSep === ',' ? '.' : ',');
          } else {
            // No fractional part in the sample (e.g. amount_no_decimals).
            fmt.decimals = 0;
            fmt.thousands = sepMatches[0] || '.';
            fmt.decimal = '';
          }
        }
        this._moneyFmt = fmt;
        return fmt;
      }

      formatMoney(cents) {
        const f = this.moneyFmt;
        const value = cents / 100;
        const fixed = value.toFixed(f.decimals);
        const [intPart, decPart] = fixed.split('.');

        const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, f.thousands === '' ? '' : f.thousands);
        let number = grouped;
        if (f.decimals > 0 && f.decimal) number += f.decimal + decPart;

        return `${f.prefix}${f.preGap}${number}${f.postGap}${f.suffix}`;
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.cta.disabled || this.cta.getAttribute('aria-disabled') === 'true') return;

        const items = this.includedAvailable().map((r) => ({
          id: Number(r.dataset.variantId),
          quantity: parseInt(r.dataset.quantity, 10) || 1,
        }));
        if (!items.length) return;

        this.setLoading(true);

        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
        };

        const body = { items };
        // Ask the cart UI which sections to re-render so the drawer/notification
        // can repaint itself, then open. Same contract product-form.js uses.
        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          body.sections = this.cart.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = JSON.stringify(body);

        const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

        fetch(`${addUrl}.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Shopify returns { status, message, description } on error.
              this.handleError(response.description || response.message);
              return;
            }

            // Notify the rest of the theme (cart count bubbles, etc.).
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bundle-builder',
                cartData: response,
              });
            }

            if (this.cart && typeof this.cart.renderContents === 'function') {
              if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              this.cart.renderContents(response);
            } else {
              // No drawer/notification on the page — go to the cart page.
              window.location = (window.routes && window.routes.cart_url) || '/cart';
            }
          })
          .catch((err) => {
            console.error('[bundle-builder] add failed', err);
            this.handleError();
          })
          .finally(() => this.setLoading(false));
      }

      setLoading(loading) {
        if (!this.cta) return;
        this.cta.classList.toggle('is-loading', loading);
        if (loading) {
          // Block re-entry while the request is in flight.
          this.cta.setAttribute('aria-disabled', 'true');
        } else {
          // Restore the real availability gate (don't blindly re-enable).
          this.refresh();
        }
      }

      handleError(message) {
        const fallback = this.dataset.errorMessage || 'A apărut o eroare. Încearcă din nou.';
        if (this.liveRegion) this.liveRegion.textContent = message || fallback;
        if (this.summary) this.summary.classList.add('has-blocker');
        const note = this.querySelector('[data-bundle-note]');
        if (note) note.textContent = message || fallback;
      }
    }
  );
}
